import ApiExceptionError from '@/entities/errors/api_errors/ApiExceptionError'
import { ResponseDataValidatorError } from '@/entities/errors/validator_errors/ResponseDataValidatorError'
import { ObjectCreationValidatorError } from '@/entities/errors/validator_errors/ObjectCreationValidatorError'
import BaseError from '@/entities/errors/api_errors/BaseError'
import BaseFormError from '@/entities/errors/form_errors/BaseFormError'
import UnauthorizedError from '@/entities/errors/api_errors/Unauthorized'
import { ErrorI } from '../../types/ErrorI'
import StripeErrorFactory from './StripeErrorsFactory'
import NetworkError, { getNetworkErrorMessage, isNetworkError } from './NetworkError'

const CLIENT_ERROR_MESSAGE = 'Your request could not be completed. Please check the details and try again.'
const SERVER_ERROR_MESSAGE = 'Something went wrong on our side. Please try again later.'

// Every error object Stripe.js produces carries one of these `type` values.
const STRIPE_ERROR_TYPES = [
  'card_error',
  'validation_error',
  'api_error',
  'idempotency_error',
  'invalid_request_error',
  'api_connection_error',
  'authentication_error',
  'rate_limit_error',
  'StripeError',
]

export default class ApiErrorFactory {
  static create(e: unknown) {
    const error = e as ErrorI

    if (e instanceof ObjectCreationValidatorError || e instanceof ResponseDataValidatorError) {
      return e
    }

    // Must run before any other mapping: a request that never reached the backend has no
    // response and no payload, so it would otherwise fall through to an unrelated handler.
    if (isNetworkError(e)) {
      return new NetworkError(getNetworkErrorMessage(e))
    }

    if (!error.response || !error.response.data) {
      if (this.isStripeError(error)) {
        StripeErrorFactory.handleStripeError(error)
        return
      }
      return new ApiExceptionError()
    }

    const { status } = error.response
    const { data } = error.response

    switch (status) {
      case 401:
        return new UnauthorizedError(data.error)

      case 422:
        return new BaseFormError(data.errors!)

      default:
        if (status >= 400 && status < 500) {
          return new BaseError(data.error || CLIENT_ERROR_MESSAGE, status)
        }
        if (status >= 500) {
          return new BaseError(data.error || SERVER_ERROR_MESSAGE, status)
        }
        return new ApiExceptionError()
    }
  }

  /**
   * Only errors raised by Stripe.js itself. A bare `code` is not enough — axios sets `code`
   * on every transport failure, which used to route offline errors into the payment handler.
   */
  private static isStripeError(error: any): boolean {
    if (!error || typeof error !== 'object') return false
    if (error.decline_code) return true
    if (typeof error.type === 'string' && STRIPE_ERROR_TYPES.includes(error.type)) return true
    return typeof error.message === 'string' && error.message.toLowerCase().includes('stripe')
  }
}
